import sharedSupabase from '../config/supabase';
import { requestComparisonRecompute } from './comparisonRecomputeService';

/**
 * Manual overrides for the relationship price comparison.
 *
 * The comparison normally resolves itself (deterministic pack parsing, then
 * one AI call — see supabase/functions/_shared/priceComparison.mjs). When a
 * person has reviewed the packages by hand, their answer outranks both: the
 * item's comparison_* columns record the chosen unit as 'manual_override' at
 * confidence 1, and every option's conversion_snapshot.price_comparison is
 * rewritten as a verified manual snapshot.
 *
 * Snapshot writes MERGE over the existing conversion_snapshot object — the
 * row is read first and sibling keys (ai_objective, package_interpretation,
 * anything else) survive. An option whose snapshot could not be read is
 * skipped rather than clobbered.
 *
 * Missing comparison columns (migration not deployed: 42703 / PGRST204)
 * degrade to a console.warn, never a throw. Every function takes an optional
 * trailing `client` (defaults to the shared supabase singleton) so tests can
 * inject a fake.
 */

// The units the manual editor offers, and the family each one implies. The
// comparison vocabulary is deliberately practical (never ml/kg, never
// case/box) — see the canonical COMPARISON_UNITS in priceComparison.mjs.
const FAMILY_BY_UNIT = {
  lb: 'mass',
  oz: 'mass',
  gal: 'volume',
  qt: 'volume',
  'fl oz': 'volume',
  each: 'count',
};

export const MANUAL_COMPARISON_UNITS = Object.keys(FAMILY_BY_UNIT);

/** 'mass' | 'volume' | 'count' for a manual comparison unit, else null. */
export function comparisonFamilyOf(unit) {
  return FAMILY_BY_UNIT[String(unit ?? '').trim().toLowerCase()] ?? null;
}

// PostgREST/Postgres "column not deployed yet" signatures.
const isMissingColumnError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return ['42703', 'PGRST204'].includes(code)
    || /column .* does not exist|schema cache/i.test(message);
};

const warnMissing = (where, error) => {
  console.warn(
    `[comparison-override] ${where} unavailable (migration not deployed?):`,
    error?.message,
  );
};

const toPositiveNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Record a manual comparison review: one relationship unit for the item, one
 * verified quantity-per-package for each option.
 *
 * @param {*} storeId
 * @param {*} inventoryItemId
 * @param {Object} spec      { unit, family?, reasoning? } — family derives
 *                           from the unit when omitted
 * @param {Array}  perOption [{ optionId, quantity }] quantity per purchase
 *                           package, in `unit`; must be positive (a manual
 *                           override is an answer, never a guess or a 0)
 * @param {Object} [client]
 */
export async function setComparisonOverride(storeId, inventoryItemId, spec = {}, perOption = [], client = sharedSupabase) {
  if (!storeId) throw new Error('storeId is required');
  if (!inventoryItemId) throw new Error('inventoryItemId is required');

  const unit = String(spec.unit ?? '').trim();
  const family = spec.family ?? comparisonFamilyOf(unit);
  if (!unit || !family) {
    throw new Error(`Comparison unit must be one of: ${MANUAL_COMPARISON_UNITS.join(', ')}.`);
  }

  const entries = (perOption || []).map((entry) => {
    const quantity = toPositiveNumber(entry?.quantity);
    if (entry?.optionId == null || quantity === null) {
      throw new Error('Every option needs a positive quantity per package.');
    }
    return { optionId: entry.optionId, quantity };
  });

  const now = new Date().toISOString();

  const { error: itemError } = await client
    .from('inventory_items')
    .update({
      comparison_unit: unit,
      comparison_unit_family: family,
      comparison_unit_source: 'manual_override',
      comparison_unit_confidence: 1,
      comparison_unit_reasoning: spec.reasoning ?? null,
      comparison_unit_updated_at: now,
    })
    .eq('id', inventoryItemId)
    .eq('store_id', storeId);
  if (itemError) {
    if (!isMissingColumnError(itemError)) throw new Error(itemError.message);
    // The per-option snapshots below still resolve the comparison (a
    // unanimous v2 unit is authoritative), so keep going.
    warnMissing('item comparison columns', itemError);
  }

  if (entries.length === 0) return { unit, family, updatedAt: now };

  // Read before write: the snapshot merge must be over what is actually
  // stored, and an option that could not be read is skipped, not clobbered.
  const { data: optionRows, error: readError } = await client
    .from('purchase_options')
    .select('id, conversion_snapshot')
    .eq('store_id', storeId)
    .eq('inventory_item_id', inventoryItemId)
    .in('id', entries.map((entry) => entry.optionId));
  if (readError) {
    if (!isMissingColumnError(readError)) throw new Error(readError.message);
    warnMissing('option conversion snapshots', readError);
    return { unit, family, updatedAt: now };
  }

  const snapshotById = new Map((optionRows || []).map((row) => [String(row.id), row.conversion_snapshot]));

  for (const entry of entries) {
    if (!snapshotById.has(String(entry.optionId))) {
      console.warn('[comparison-override] option not in this relationship, skipped:', entry.optionId);
      continue;
    }
    const existing = snapshotById.get(String(entry.optionId));
    const base = existing && typeof existing === 'object' ? existing : {};
    const merged = {
      ...base,
      price_comparison: {
        version: 2,
        relationship_unit: unit,
        unit_family: family,
        quantity_per_purchase_package: entry.quantity,
        source: 'manual_override',
        confidence: 1,
        verified: true,
        calculated_at: now,
      },
    };
    // eslint-disable-next-line no-await-in-loop
    const { error: writeError } = await client
      .from('purchase_options')
      .update({ conversion_snapshot: merged })
      .eq('id', entry.optionId);
    if (writeError) {
      if (!isMissingColumnError(writeError)) throw new Error(writeError.message);
      warnMissing('option conversion snapshot write', writeError);
    }
  }

  return { unit, family, updatedAt: now };
}

/**
 * Remove the manual override: null every comparison_* column on the item,
 * then ask the server to re-resolve automatically. The recompute is
 * fire-and-forget — clearing already succeeded, and the stored per-option
 * snapshots keep the display coherent until the fresh resolution lands.
 */
export async function clearComparisonOverride(storeId, inventoryItemId, client = sharedSupabase) {
  if (!storeId) throw new Error('storeId is required');
  if (!inventoryItemId) throw new Error('inventoryItemId is required');

  const { error } = await client
    .from('inventory_items')
    .update({
      comparison_unit: null,
      comparison_unit_family: null,
      comparison_unit_source: null,
      comparison_unit_confidence: null,
      comparison_unit_reasoning: null,
      comparison_unit_updated_at: null,
    })
    .eq('id', inventoryItemId)
    .eq('store_id', storeId);
  if (error) {
    if (!isMissingColumnError(error)) throw new Error(error.message);
    warnMissing('item comparison columns', error);
  }

  void requestComparisonRecompute(storeId, inventoryItemId, client);
  return true;
}
